import type { RentalAgreementDto, TenantDto } from '../../services/api';
import { t as translate } from '../../services/i18n';
import { getCandidatePortalStatusConfig } from '../candidate/candidate-shared';
import { escapeHtml, hasCurrentAgreement, TENANT_ICONS } from './tenant-shared';
import type { AccordionTab, PaymentHistoryWindow, TenantAccordionState } from './tenant-types';
import './rc-tenant-accordion';

export class RcTenantListView extends HTMLElement {
  tenants: TenantDto[] = [];
  hasTenants = false;
  filters = {
    name: '',
    status: '',
    propertyId: '',
  };
  propertyOptions: Array<{ id: string; address: string; displayName: string }> = [];
  allAgreementsByTenant = new Map<string, RentalAgreementDto[]>();
  expandedTenantIds = new Set<string>();
  accordionStates = new Map<string, TenantAccordionState>();
  paymentHistoryCache = new Map<string, PaymentHistoryWindow>();
  paymentHistoryErrors = new Set<string>();

  connectedCallback(): void {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.handleChange);
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.handleViewportChange);
    document.addEventListener('scroll', this.handleViewportChange, true);
    this.render();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('input', this.handleInput);
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.handleViewportChange);
    document.removeEventListener('scroll', this.handleViewportChange, true);
  }

  render(): void {
    this.innerHTML = `
      <div class="page-header">
        <h1>${translate('tenantPage.title')}</h1>
        <button class="btn btn-primary" data-action="add">
          ${TENANT_ICONS.plus}
          ${translate('tenantPage.addButton')}
        </button>
      </div>
      ${this.hasTenants ? this.renderFilters() : ''}
      ${!this.hasTenants
        ? `<div class="empty-state card">
            ${TENANT_ICONS.userLg}
            <p>${translate('tenantPage.empty')}</p>
            <button class="btn btn-primary" data-action="add">${translate('tenantPage.addFirst')}</button>
          </div>`
        : this.tenants.length === 0
        ? `<div class="empty-state card">
            ${TENANT_ICONS.userLg}
            <p>${translate('tenantPage.noResults')}</p>
            <button class="btn btn-outline" data-action="reset-filters">${translate('tenantPage.filters.reset')}</button>
          </div>`
        : `<div class="card table-wrapper">
            <div class="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style="width:2rem;padding-right:0;"></th>
                    <th>${translate('common.name')}</th>
                    <th>${translate('common.status')}</th>
                    <th style="width:3rem;padding-left:0;"></th>
                  </tr>
                </thead>
                <tbody>
                  ${this.tenants.map((tenant) => `
                    <tr class="tenant-row" data-tenant-row-id="${tenant.id}">
                      <td style="padding-right:0;">
                        <button class="btn-toggle" data-action="toggle" data-id="${tenant.id}" aria-expanded="${this.expandedTenantIds.has(tenant.id)}">${TENANT_ICONS.chevron}</button>
                      </td>
                      <td>
                        <div class="tenant-name" style="cursor:pointer;" data-action="toggle" data-id="${tenant.id}">${escapeHtml(tenant.firstName)} ${escapeHtml(tenant.lastName)}</div>
                        <div class="tenant-inline-meta">
                          ${this.renderTenantPortalStatusBadge(tenant)}
                          ${this.renderWelcomePageBadge(tenant)}
                        </div>
                      </td>
                      <td><span class="badge ${this.isTenantCurrentlyActive(tenant.id) ? 'badge-success' : 'badge-muted'}">${this.isTenantCurrentlyActive(tenant.id) ? translate('common.active') : translate('common.ended')}</span></td>
                      <td style="padding-left:0;">
                        <div class="tenant-menu-container" data-menu-id="${tenant.id}">
                          <button class="btn-icon tenant-menu-btn" data-action="open-menu" data-id="${tenant.id}" aria-label="${translate('tenantPage.moreActions')}" title="${translate('tenantPage.moreActions')}">
                            ${TENANT_ICONS.moreHoriz}
                          </button>
                          <div class="tenant-menu-dropdown" role="menu">
                            <button class="tenant-menu-item" data-action="edit" data-id="${tenant.id}" role="menuitem">
                              ${TENANT_ICONS.edit}
                              ${translate('common.edit')}
                            </button>
                            <div class="tenant-menu-divider"></div>
                            <button class="tenant-menu-item tenant-menu-item--danger" data-action="delete" data-id="${tenant.id}" role="menuitem">
                              ${translate('common.delete')}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr class="accordion-row" data-accordion-id="${tenant.id}" style="${this.expandedTenantIds.has(tenant.id) ? '' : 'display:none;'}">
                      <td colspan="4" id="accordion-cell-${tenant.id}">
                        <div class="accordion-body">
                          <rc-tenant-accordion data-tenant-id="${tenant.id}"></rc-tenant-accordion>
                        </div>
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>`}
    `;

    this.tenants.forEach((tenant) => {
      const accordion = this.querySelector<HTMLElement>(`rc-tenant-accordion[data-tenant-id="${tenant.id}"]`) as (HTMLElement & {
        tenant?: TenantDto | null;
        agreements?: TenantAccordionState['agreements'];
        loading?: boolean;
        error?: boolean;
        activeTab?: AccordionTab;
        paymentHistoryCache?: Map<string, PaymentHistoryWindow>;
        paymentHistoryErrors?: Set<string>;
        highlightSurveyId?: string | null;
        render?: () => void;
      }) | null;
      if (!accordion) return;

      const state = this.accordionStates.get(tenant.id);
      customElements.upgrade(accordion);
      accordion.tenant = tenant;
      accordion.agreements = state?.agreements ?? [];
      accordion.loading = state?.loading ?? false;
      accordion.error = state?.error ?? false;
      accordion.activeTab = state?.activeTab ?? 'info';
      accordion.paymentHistoryCache = this.paymentHistoryCache;
      accordion.paymentHistoryErrors = this.paymentHistoryErrors;
      accordion.render?.();
    });
  }

  private renderFilters(): string {
    const hasActiveFilters = this.hasActiveFilters();

    return `
      <div class="card tenant-filters">
        <div class="tenant-filters-bar">
          <div class="tenant-filters-fields">
            <div class="tenant-filter-field tenant-filter-field--search">
              <label class="sr-only" for="tenant-filter-name">${translate('tenantPage.filters.nameLabel')}</label>
            <input
              class="tenant-filter-control"
              id="tenant-filter-name"
              name="name"
              type="text"
              value="${escapeHtml(this.filters.name)}"
              placeholder="${translate('tenantPage.filters.namePlaceholder')}"
              aria-label="${translate('tenantPage.filters.nameLabel')}"
            />
          </div>
            <div class="tenant-filter-field">
              <label class="sr-only" for="tenant-filter-status">${translate('tenantPage.filters.statusLabel')}</label>
              <select class="tenant-filter-control" id="tenant-filter-status" name="status" aria-label="${translate('tenantPage.filters.statusLabel')}">
                <option value="">${translate('tenantPage.filters.allStatuses')}</option>
              <option value="active" ${this.filters.status === 'active' ? 'selected' : ''}>${translate('common.active')}</option>
              <option value="ended" ${this.filters.status === 'ended' ? 'selected' : ''}>${translate('common.ended')}</option>
            </select>
          </div>
            <div class="tenant-filter-field">
              <label class="sr-only" for="tenant-filter-property">${translate('tenantPage.filters.propertyLabel')}</label>
              <select class="tenant-filter-control" id="tenant-filter-property" name="propertyId" aria-label="${translate('tenantPage.filters.propertyLabel')}">
                <option value="">${translate('tenantPage.filters.allProperties')}</option>
              ${this.propertyOptions.map((property) => `
                <option value="${property.id}" ${this.filters.propertyId === property.id ? 'selected' : ''}>${escapeHtml(property.displayName || property.address)}</option>
              `).join('')}
            </select>
          </div>
          </div>
          ${hasActiveFilters
            ? `<button class="btn btn-outline btn-sm tenant-filters-reset" data-action="reset-filters">${translate('tenantPage.filters.reset')}</button>`
            : ''}
        </div>
      </div>
    `;
  }

  private handleClick = (event: Event): void => {
    const inMenuContainer = !!(event.target as HTMLElement)?.closest?.('.tenant-menu-container');
    if (!inMenuContainer) this.closeAllMenus();

    const trigger = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (!trigger) return;

    const tenantId = trigger.dataset.id;
    switch (trigger.dataset.action) {
      case 'add':
        this.dispatchSliceEvent('tenant-add', {});
        break;
      case 'reset-filters':
        this.dispatchFilterChange({ name: '', status: '', propertyId: '' });
        break;
      case 'open-menu':
        if (tenantId) this.toggleMenu(tenantId);
        break;
      case 'toggle':
        if (tenantId) this.dispatchSliceEvent('tenant-toggle', { tenantId });
        break;
      case 'portal-link-badge':
        event.stopPropagation();
        if (tenantId) this.dispatchSliceEvent('tenant-link-portal-account', { tenantId });
        break;
      case 'portal-invite-badge':
        event.stopPropagation();
        if (tenantId) this.dispatchSliceEvent('tenant-invite-portal-account', { tenantId });
        break;
      case 'edit':
        this.closeAllMenus();
        if (tenantId) this.dispatchSliceEvent('tenant-edit', { tenantId });
        break;
      case 'delete':
        this.closeAllMenus();
        if (tenantId) this.dispatchSliceEvent('tenant-delete', { tenantId });
        break;
      default:
        break;
    }
  };

  private handleDocumentClick = (event: Event): void => {
    if (!this.contains(event.target as Node)) {
      this.closeAllMenus();
    }
  };

  private handleViewportChange = (): void => {
    this.closeAllMenus();
  };

  private handleInput = (event: Event): void => {
    const target = event.target as HTMLInputElement | null;
    if (!target || target.name !== 'name') {
      return;
    }

    this.dispatchFilterChange({
      name: target.value,
      status: this.filters.status,
      propertyId: this.filters.propertyId,
    });
  };

  private handleChange = (event: Event): void => {
    const target = event.target as HTMLSelectElement | null;
    if (!target || (target.name !== 'status' && target.name !== 'propertyId')) {
      return;
    }

    this.dispatchFilterChange({
      name: this.filters.name,
      status: target.name === 'status' ? target.value : this.filters.status,
      propertyId: target.name === 'propertyId' ? target.value : this.filters.propertyId,
    });
  };

  private toggleMenu(tenantId: string): void {
    const container = this.querySelector<HTMLElement>(`.tenant-menu-container[data-menu-id="${tenantId}"]`);
    if (!container) return;
    const isOpen = container.hasAttribute('data-open');
    this.closeAllMenus();
    if (!isOpen) {
      container.setAttribute('data-open', '');
      this.positionMenu(container);
    }
  }

  private closeAllMenus(): void {
    this.querySelector('.table-scroll')?.classList.remove('table-scroll--menu-open');
    this.querySelectorAll<HTMLElement>('.tenant-menu-container[data-open]').forEach((el) => {
      el.removeAttribute('data-open');
      delete el.dataset.menuPlacement;
    });
  }

  private positionMenu(container: HTMLElement): void {
    this.querySelector('.table-scroll')?.classList.add('table-scroll--menu-open');
    const dropdown = container.querySelector<HTMLElement>('.tenant-menu-dropdown');
    if (!dropdown) return;

    const containerRect = container.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const spaceBelow = Math.max(0, viewportHeight - containerRect.bottom);
    const spaceAbove = Math.max(0, containerRect.top);
    const shouldOpenUpwards = dropdownRect.height > spaceBelow && spaceAbove > spaceBelow;

    container.dataset.menuPlacement = shouldOpenUpwards ? 'top' : 'bottom';
  }

  private dispatchSliceEvent(name: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail,
    }));
  }

  private dispatchFilterChange(filters: { name: string; status: string; propertyId: string }): void {
    this.dispatchSliceEvent('tenant-filter-change', filters);
  }

  private renderWelcomePageBadge(tenant: TenantDto): string {
    if (!tenant.welcomePage?.isConfigured) {
      return `<span class="badge badge-muted tenant-inline-badge">${translate('tenantWelcome.status.notConfigured')}</span>`;
    }

    if (!tenant.welcomePage.isTenantCurrent) {
      return `<span class="badge badge-muted tenant-inline-badge">${translate('tenantWelcome.status.ended')}</span>`;
    }

    if (tenant.welcomePage.isActive) {
      return `<span class="badge badge-success tenant-inline-badge">${translate('tenantWelcome.status.active')}</span>`;
    }

    return `<span class="badge badge-muted tenant-inline-badge">${translate('tenantWelcome.status.inactive')}</span>`;
  }

  private renderTenantPortalStatusBadge(tenant: TenantDto): string {
    const portalStatus = getCandidatePortalStatusConfig(tenant.tenantPortalConnectionStatus);
    const badgeContent = `${portalStatus.icon} ${escapeHtml(portalStatus.label)}`;

    if (tenant.tenantPortalConnectionStatus === 'has-account-unlinked') {
      return `<button type="button" class="badge ${portalStatus.badgeClass} tenant-inline-badge tenant-inline-badge-button" data-action="portal-link-badge" data-id="${tenant.id}" title="${escapeHtml(portalStatus.tooltip)}">${badgeContent}</button>`;
    }

    if (tenant.tenantPortalConnectionStatus === 'no-account-unlinked' && tenant.email) {
      return `<button type="button" class="badge ${portalStatus.badgeClass} tenant-inline-badge tenant-inline-badge-button" data-action="portal-invite-badge" data-id="${tenant.id}" title="${escapeHtml(portalStatus.tooltip)}">${badgeContent}</button>`;
    }

    return `<span class="badge ${portalStatus.badgeClass} tenant-inline-badge" title="${escapeHtml(portalStatus.tooltip)}">${badgeContent}</span>`;
  }

  private isTenantCurrentlyActive(tenantId: string): boolean {
    return hasCurrentAgreement(this.allAgreementsByTenant.get(tenantId) ?? []);
  }

  private hasActiveFilters(): boolean {
    return this.filters.name.trim().length > 0
      || this.filters.status.length > 0
      || this.filters.propertyId.length > 0;
  }
}

if (!customElements.get('rc-tenant-list-view')) {
  customElements.define('rc-tenant-list-view', RcTenantListView);
}
